import {
  Palette as CardIconPalette,
  Sun as CardIconSun,
  Rows3 as CardIconRows3,
} from 'lucide-react';
import { CardHeader as ContentCardHeader } from '@hue/design-system';
import { Card } from '@hue/design-system';
import { Link } from '@tanstack/react-router';
import { Check, Moon, Sun } from 'lucide-react';
import { useCompanyScope } from './company-context.js';
import { PALETTES, useAppearance } from './appearance.js';
import { PageHeader } from './page-title.js';

/// Aparência dentro de Configurações (ADR 0041, decisão 4): tema, paleta e densidade são da pessoa.
///
/// É a mesma preferência do ícone de cores da casca, lida e gravada pelo mesmo `useAppearance` —
/// trocar aqui muda o ícone na hora, e o contrário também. A cor da marca é da empresa e não mora
/// aqui: quem pode mexer nela recebe o caminho para Dados da empresa.
export function CompanyAppearanceScreen() {
  const { user, company, can } = useCompanyScope();
  const { mode, palette, density, choose } = useAppearance(user);

  return (
    <section aria-labelledby="appearance-title">
      <PageHeader
        trail="Configurações / Aparência"
        id="appearance-title"
        title="Aparência"
        description={
          <>
            Como o sistema aparece para você, em qualquer navegador em que entrar. Não muda o que as
            outras pessoas veem.
          </>
        }
      />
      <div className="grid grid-cols-[minmax(0,1fr)] gap-6">
        <Card className="grid gap-4 p-5 sm:p-6">
          <div>
            <ContentCardHeader title="Modo" icon={<CardIconSun className="size-4" />} />
            <p className="mt-1 text-xs text-content-muted">Fundo claro ou escuro.</p>
          </div>
          <div
            role="radiogroup"
            aria-label="Modo"
            className="grid grid-cols-[minmax(0,1fr)] gap-2 sm:grid-cols-2"
          >
            {(
              [
                { value: 'light', label: 'Claro', icon: Sun },
                { value: 'dark', label: 'Escuro', icon: Moon },
              ] as const
            ).map((option) => {
              const selected = option.value === mode;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => choose({ mode: option.value })}
                  className={`flex min-h-12 items-center gap-3 rounded-xl border px-4 text-left text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 ${
                    selected
                      ? 'border-brand-500/40 bg-brand-500/10 text-content'
                      : 'border-surface-border text-content-muted hover:border-brand-500/40 hover:text-content'
                  }`}
                >
                  <option.icon className="size-4 shrink-0" aria-hidden="true" />
                  <span className="flex-1">{option.label}</span>
                  {selected ? <Check className="size-4 text-brand-400" aria-hidden="true" /> : null}
                </button>
              );
            })}
          </div>
        </Card>

        <Card className="grid gap-4 p-5 sm:p-6">
          <div>
            <ContentCardHeader
              title="Tema de cores"
              icon={<CardIconPalette className="size-4" />}
            />
            <p className="mt-1 text-xs text-content-muted">
              A cor dos botões, destaques e seleções da interface.
            </p>
          </div>
          <div
            role="radiogroup"
            aria-label="Tema de cores"
            className="grid grid-cols-[minmax(0,1fr)] gap-2 sm:grid-cols-2"
          >
            {PALETTES.map((option) => {
              const selected = option.value === palette;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => choose({ palette: option.value })}
                  className={`flex min-h-14 items-center gap-3 rounded-xl border px-3 py-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 ${
                    selected
                      ? 'border-brand-500/40 bg-brand-500/10'
                      : 'border-surface-border hover:border-brand-500/40'
                  }`}
                >
                  <span className="flex shrink-0 overflow-hidden rounded-full border border-surface-border">
                    {option.swatches.map((color) => (
                      <span key={color} className="size-5" style={{ backgroundColor: color }} />
                    ))}
                  </span>
                  <span className="min-w-0 flex-1">
                    <strong className="block text-sm">{option.label}</strong>
                    <span className="block truncate text-xs text-content-muted">
                      {option.description}
                    </span>
                  </span>
                  {selected ? (
                    <Check className="size-4 shrink-0 text-brand-400" aria-hidden="true" />
                  ) : null}
                </button>
              );
            })}
          </div>
        </Card>

        <Card className="p-5 sm:p-6">
          <ContentCardHeader title="Densidade" icon={<CardIconRows3 className="size-4" />} />
          <button
            type="button"
            role="switch"
            aria-checked={density === 'compact'}
            onClick={() => choose({ density: density === 'compact' ? 'comfortable' : 'compact' })}
            className="flex w-full items-center justify-between gap-4 rounded-xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
          >
            <span>
              <strong className="block text-sm font-semibold">Versão compacta</strong>
              <span className="mt-1 block text-xs text-content-muted">
                Menos espaço entre os elementos, mais conteúdo na tela.
              </span>
            </span>
            <span
              aria-hidden="true"
              className={`flex h-6 w-11 shrink-0 items-center rounded-full p-0.5 transition ${
                density === 'compact' ? 'justify-end bg-brand-500' : 'bg-surface-border'
              }`}
            >
              <span className="size-5 rounded-full bg-white shadow" />
            </span>
          </button>
        </Card>

        {can('company_profile.read') ? (
          <p className="text-sm text-content-muted">
            A cor e as logos da empresa, usadas no menu e nos documentos, ficam em{' '}
            <Link
              to="/companies/$companyKey/settings/profile"
              params={{ companyKey: company.key }}
              className="font-medium text-brand-400 underline-offset-4 hover:underline"
            >
              Dados da empresa
            </Link>
            .
          </p>
        ) : null}
      </div>
    </section>
  );
}
